import { Button } from '@Components/button/button'

import './reason-for-visit.scss'
import { Selector } from '@Components/selector/selector'
import { FormFloatingFooter } from '@Components/claim-flow/form-floating-footer'
import { REASON_FOR_VISIT } from '@Components/claim-flow/types'
import { ComponentUtils } from '@Components/utils'
import { Form } from 'radix-ui'
import { useCallback, useState } from 'react'
import { useHostConfig } from '../../../../hooks/use-host-config'
import { ClaimReservationCommunicationMethod } from '@interaction-gaming/player-data-service-browser-client'

const CLASS_NAME = 'reason-for-visit'

export type ReasonForVisitData = {
  numberOfTickets: number
  reasonForVisit: REASON_FOR_VISIT
  communicationMethod: ClaimReservationCommunicationMethod
}

type ReasonForVisitProps = {
  onNext: (data: ReasonForVisitData) => void
}

export const ReasonForVisitScreen = ({ onNext }: ReasonForVisitProps) => {
  const [numberOfTickets, setNumberOfTickets] = useState<number | undefined>(undefined)
  const [reasonForVisit, setReasonForVisit] = useState<REASON_FOR_VISIT | undefined>(undefined)
  const [communicationMethod, setCommunicationMethod] =
    useState<ClaimReservationCommunicationMethod>(ClaimReservationCommunicationMethod.PHONE)

  const actionHandler = useCallback(() => {
    if (!numberOfTickets || !reasonForVisit) {
      return
    }
    const formData: ReasonForVisitData = {
      numberOfTickets,
      reasonForVisit,
      communicationMethod,
    }

    onNext(formData)
  }, [onNext, numberOfTickets, reasonForVisit, communicationMethod])

  const hostConfig = useHostConfig()

  return (
    <section className={CLASS_NAME}>
      <Form.Root asChild action={actionHandler}>
        <Form.Form className={`${CLASS_NAME}__form`}>
          <Form.Field name="reasonForVisit" className={`${CLASS_NAME}__field`}>
            <Form.Label>What is the value of your largest ticket?*</Form.Label>

            <Form.Control asChild required>
              <Selector<REASON_FOR_VISIT>
                ariaLabel={'What is the value of your largest ticket?'}
                placeHolderText={''}
                value={reasonForVisit}
                required
                selectables={[
                  {
                    label: `${ComponentUtils.formatCentsToUSD(hostConfig.standardFlowRangeInCents.min)} - ${ComponentUtils.formatCentsToUSD(hostConfig.standardFlowRangeInCents.max)}`,
                    value: REASON_FOR_VISIT.STANDARD_CLAIM,
                  },
                  {
                    label: `${ComponentUtils.formatCentsToUSD(hostConfig.standardFlowRangeInCents.max + 1)} or More`,
                    value: REASON_FOR_VISIT.BIG_WINNER,
                  },
                  {
                    label: `${ComponentUtils.formatCentsToUSD(100)} - ${ComponentUtils.formatCentsToUSD(hostConfig.standardFlowRangeInCents.min - 1)}`,
                    value: REASON_FOR_VISIT.REGULAR_TICKET,
                  },
                  {
                    label: 'Damaged',
                    value: REASON_FOR_VISIT.PROBLEM_WITH_TICKET,
                  },
                  {
                    label: 'Unknown',
                    value: REASON_FOR_VISIT.UNKNOWN_WIN,
                  },
                ]}
                onValueChange={setReasonForVisit}
              />
            </Form.Control>

            {hostConfig.reasonForVisitScreen.prizeAmountDisclaimer}
          </Form.Field>

          <Form.Field name="numberOfTickets" className={`${CLASS_NAME}__field`}>
            <Form.Label className={`${CLASS_NAME}__label`}>Number of tickets*</Form.Label>
            <Form.Message className={`${CLASS_NAME}__message`} match="valueMissing">
              Please enter the number of tickets you have to claim.
            </Form.Message>
            <Form.Message className={`${CLASS_NAME}__message`} match="typeMismatch">
              Please enter a valid number
            </Form.Message>
            <Form.Control name="numberOfTickets" asChild required>
              <Selector<number>
                ariaLabel={'Number of tickets'}
                placeHolderText={''}
                value={numberOfTickets ? `${numberOfTickets}` : undefined}
                required
                selectables={Array.from({ length: 15 }, (_, i) => {
                  const value = i + 1
                  return {
                    value,
                    label: value === 15 ? '15+' : value.toString(),
                  }
                })}
                onValueChange={setNumberOfTickets}
              />
            </Form.Control>
          </Form.Field>

          <Form.Field name="communicationMethod" className={`${CLASS_NAME}__field`}>
            <Form.Label className={`${CLASS_NAME}__label`}>
              What is your preferred communication method?*
            </Form.Label>
            <Form.Control name="communicationMethod" asChild required>
              <Selector<ClaimReservationCommunicationMethod>
                ariaLabel={'What is your preferred communication method?'}
                placeHolderText={''}
                value={communicationMethod}
                required
                selectables={[
                  {
                    value: ClaimReservationCommunicationMethod.PHONE,
                    label: 'Text',
                  },
                  {
                    value: ClaimReservationCommunicationMethod.EMAIL,
                    label: 'Email',
                  },
                ]}
                onValueChange={setCommunicationMethod}
              />
            </Form.Control>
          </Form.Field>
        </Form.Form>
      </Form.Root>

      <FormFloatingFooter>
        <section className={`floating-footer__buttons`}>
          <Button
            className={`floating-footer__next-button`}
            disabled={!reasonForVisit || !numberOfTickets}
            onClick={actionHandler}>
            Next
          </Button>
        </section>
      </FormFloatingFooter>
    </section>
  )
}
